import { create } from 'zustand';
import { adminProductDetailApi } from '@api/api-config.ts';
import {
  AdminProductDetailResponse,
  AdminProductAuctionResponse,
  AdminProductSupplierResponse,
  AdminProductCustomerResponse,
  AdminProductPriceResponse,
  AdminProductImagesResponse,
  AdminCreatePriceRequest,
  AdminUpdatePriceVariantRequest,
  AdminAddPriceVisibilityRequest,
  AdminProductAuctionLovResponse,
  AdminProductSupplierLovResponse,
  AdminProductCustomerLovResponse,
  PriceType,
} from '@api/generated/portal-be-admin';

interface PaginationState {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface AdminProductDetailStoreState {
  // Základní detail
  loading: boolean;
  error: string | null;
  product: AdminProductDetailResponse | null;

  // Obrázky
  images: AdminProductImagesResponse | null;
  imagesLoading: boolean;
  imagesError: string | null;

  // Aukce
  auctions: AdminProductAuctionResponse[];
  auctionsLoading: boolean;
  auctionsError: string | null;
  auctionsPagination: PaginationState | null;

  // Dodavatelé
  suppliers: AdminProductSupplierResponse[];
  suppliersLoading: boolean;
  suppliersError: string | null;
  suppliersPagination: PaginationState | null;

  // Zákazníci
  customers: AdminProductCustomerResponse[];
  customersLoading: boolean;
  customersError: string | null;
  customersPagination: PaginationState | null;

  // Ceny
  prices: AdminProductPriceResponse[];
  pricesLoading: boolean;
  pricesError: string | null;
  pricesPagination: PaginationState | null;

  /** Nabídky do filtrů nad záložkou Ceny - celý seznam, ne jen zobrazená stránka záložky. */
  auctionLov: AdminProductAuctionLovResponse[];
  supplierLov: AdminProductSupplierLovResponse[];
  customerLov: AdminProductCustomerLovResponse[];

  // Akce
  loadProductDetail: (productId: number) => Promise<void>;
  loadImages: (productId: number) => Promise<void>;
  loadAuctions: (productId: number, params: { page?: number; size?: number; q?: string }) => Promise<void>;
  loadSuppliers: (productId: number, params: { page?: number; size?: number; q?: string }) => Promise<void>;
  loadCustomers: (productId: number, params: { page?: number; size?: number; q?: string }) => Promise<void>;
  loadFilterLov: (productId: number) => Promise<void>;
  loadPrices: (
    productId: number,
    params: {
      page?: number;
      size?: number;
      auctionId?: number;
      supplierId?: number;
      customerId?: number;
      priceType?: PriceType;
      isGuaranteed?: boolean;
    },
  ) => Promise<void>;

  // Ruční správa cen
  createPrice: (productId: number, request: AdminCreatePriceRequest) => Promise<void>;
  updatePriceVariant: (
    productId: number,
    priceId: number,
    variantId: number,
    request: AdminUpdatePriceVariantRequest,
  ) => Promise<void>;
  addPriceVisibility: (productId: number, priceId: number, request: AdminAddPriceVisibilityRequest) => Promise<void>;
  deletePriceVisibility: (productId: number, priceId: number, visibilityId: number) => Promise<void>;
  deletePrice: (productId: number, priceId: number) => Promise<void>;

  reset: () => void;
}

export const useAdminProductDetailStore = create<AdminProductDetailStoreState>(set => ({
  // Základní detail
  loading: false,
  error: null,
  product: null,

  // Obrázky
  images: null,
  imagesLoading: false,
  imagesError: null,

  // Aukce
  auctions: [],
  auctionsLoading: false,
  auctionsError: null,
  auctionsPagination: null,

  // Dodavatelé
  suppliers: [],
  suppliersLoading: false,
  suppliersError: null,
  suppliersPagination: null,

  // Zákazníci
  customers: [],
  customersLoading: false,
  customersError: null,
  customersPagination: null,

  // Ceny
  prices: [],
  pricesLoading: false,
  pricesError: null,
  pricesPagination: null,

  auctionLov: [],
  supplierLov: [],
  customerLov: [],

  loadProductDetail: async (productId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await adminProductDetailApi.getProductDetail({ productId });
      set({
        loading: false,
        error: null,
        product: response.data,
      });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Chyba při načítání detailu produktu',
        product: null,
      });
    }
  },

  loadImages: async (productId: number) => {
    try {
      set({ imagesLoading: true, imagesError: null });
      const response = await adminProductDetailApi.getProductImages({ productId });
      set({
        imagesLoading: false,
        imagesError: null,
        images: response.data,
      });
    } catch (error) {
      set({
        imagesLoading: false,
        imagesError: error instanceof Error ? error.message : 'Chyba při načítání obrázků',
        images: null,
      });
    }
  },

  loadAuctions: async (productId: number, params) => {
    try {
      set({ auctionsLoading: true, auctionsError: null });
      const response = await adminProductDetailApi.getProductAuctions({
        productId,
        page: params.page || 1,
        size: params.size || 20,
        q: params.q,
      });
      set({
        auctionsLoading: false,
        auctionsError: null,
        auctions: response.data.items,
        auctionsPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({
        auctionsLoading: false,
        auctionsError: error instanceof Error ? error.message : 'Chyba při načítání aukcí',
        auctions: [],
        auctionsPagination: null,
      });
    }
  },

  loadSuppliers: async (productId: number, params) => {
    try {
      set({ suppliersLoading: true, suppliersError: null });
      const response = await adminProductDetailApi.getProductSuppliers({
        productId,
        page: params.page || 1,
        size: params.size || 20,
        q: params.q,
      });
      set({
        suppliersLoading: false,
        suppliersError: null,
        suppliers: response.data.items,
        suppliersPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({
        suppliersLoading: false,
        suppliersError: error instanceof Error ? error.message : 'Chyba při načítání dodavatelů',
        suppliers: [],
        suppliersPagination: null,
      });
    }
  },

  loadCustomers: async (productId: number, params) => {
    try {
      set({ customersLoading: true, customersError: null });
      const response = await adminProductDetailApi.getProductCustomers({
        productId,
        page: params.page || 1,
        size: params.size || 20,
        q: params.q,
      });
      set({
        customersLoading: false,
        customersError: null,
        customers: response.data.items,
        customersPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({
        customersLoading: false,
        customersError: error instanceof Error ? error.message : 'Chyba při načítání zákazníků',
        customers: [],
        customersPagination: null,
      });
    }
  },

  loadFilterLov: async (productId: number) => {
    try {
      const [auctionsResponse, suppliersResponse, customersResponse] = await Promise.all([
        adminProductDetailApi.getProductAuctionLov({ productId }),
        adminProductDetailApi.getProductSupplierLov({ productId }),
        adminProductDetailApi.getProductCustomerLov({ productId }),
      ]);
      set({
        auctionLov: auctionsResponse.data || [],
        supplierLov: suppliersResponse.data || [],
        customerLov: customersResponse.data || [],
      });
    } catch {
      set({ auctionLov: [], supplierLov: [], customerLov: [] });
    }
  },

  loadPrices: async (productId: number, params) => {
    try {
      set({ pricesLoading: true, pricesError: null });
      const response = await adminProductDetailApi.getProductPrices({
        productId,
        page: params.page || 1,
        size: params.size || 20,
        auctionId: params.auctionId,
        supplierId: params.supplierId,
        customerId: params.customerId,
        priceType: params.priceType,
        isGuaranteed: params.isGuaranteed,
      });
      set({
        pricesLoading: false,
        pricesError: null,
        prices: response.data.items,
        pricesPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({
        pricesLoading: false,
        pricesError: error instanceof Error ? error.message : 'Chyba při načítání cen',
        prices: [],
        pricesPagination: null,
      });
    }
  },

  createPrice: async (productId, request) => {
    await adminProductDetailApi.createProductPrice({ productId, adminCreatePriceRequest: request });
  },

  updatePriceVariant: async (productId, priceId, variantId, request) => {
    await adminProductDetailApi.updateProductPriceVariant({
      productId,
      priceId,
      variantId,
      adminUpdatePriceVariantRequest: request,
    });
  },

  addPriceVisibility: async (productId, priceId, request) => {
    await adminProductDetailApi.addPriceVisibility({
      productId,
      priceId,
      adminAddPriceVisibilityRequest: request,
    });
  },

  deletePriceVisibility: async (productId, priceId, visibilityId) => {
    await adminProductDetailApi.deletePriceVisibility({ productId, priceId, visibilityId });
  },

  deletePrice: async (productId, priceId) => {
    await adminProductDetailApi.deleteProductPrice({ productId, priceId });
  },

  reset: () => {
    set({
      loading: false,
      error: null,
      product: null,
      images: null,
      imagesLoading: false,
      imagesError: null,
      auctions: [],
      auctionsLoading: false,
      auctionsError: null,
      auctionsPagination: null,
      suppliers: [],
      suppliersLoading: false,
      suppliersError: null,
      suppliersPagination: null,
      customers: [],
      customersLoading: false,
      customersError: null,
      customersPagination: null,
      prices: [],
      pricesLoading: false,
      pricesError: null,
      pricesPagination: null,
      auctionLov: [],
      supplierLov: [],
      customerLov: [],
    });
  },
}));
